import { useContext, useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, InputNumber, Select } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { FormRow } from "../../../../components/form/form-row";
import {
  TContext,
  TDropdownOptions,
  TResponseModel,
} from "../../../../types/config";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { TProduct } from "../../../../types";
import {
  AddEditProduct,
  GetCustomerList,
  GetSpecificProduct,
} from "../../../../utils/api.constant";
import { UserContext } from "../../../../context";
import "../../../../index.css";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import Utils from "../../../../utils";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

export default function ProductForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );

  const { post } = useFetch();
  const { onShowToast } = useToast();

  const defaultValues: Partial<TProduct> = {
    id: 0,
    airtechModel: "",
    description: "",
    bePartNo: "",
    warrantyDuration: 0,
    price: 0,
    platform: "",
    acronym: "",
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchSpecificProduct();
      await fetchCustomerOptions();
    };

    fetchData();
  }, []);

  const onFinish = async (values: TProduct) => {
    try {
      const payload = {
        data: {
          ...values,
          id: props.id ? props.id : 0,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditProduct, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpecificProduct = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response: TResponseModel = await post(
          GetSpecificProduct,
          payload
        );
        if (response) {
          const { data, dataResponse } = response;
          const { returnCode, description } = dataResponse;
          if (returnCode === eResultCode.SUCCESS) {
            form.setFieldsValue({ ...data });
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.leftBottom,
              content: description,
            });
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchCustomerOptions = async () => {
    try {
      const payload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };
      setIsLoading(true);
      const response: TResponseModel = await post(GetCustomerList, payload);
      if (response) {
        const { data, dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setCustomerOptions(Utils.mapDropdownOptions(data));
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <Row
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Col>
              <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
                {`${props.id ? "Edit" : "Add"} a Product`}
              </h3>
            </Col>
            <Col>
              <Button
                type="default"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => {
                  props.onCloseDrawer();
                }}
              >
                Close
              </Button>
            </Col>
          </Row>
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="customerId"
                label="Customer"
                rules={[
                  {
                    required: true,
                    message: "Please select a customer!",
                  },
                ]}
              >
                <Select
                  showSearch
                  size="large"
                  disabled={isLoading}
                  options={customerOptions}
                  placeholder="Select a customer"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="airtechModel"
                label="Airtech Model"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Enter Airtech model"),
                  },
                ]}
              >
                <Input
                  maxLength={100}
                  size="large"
                  disabled={isLoading}
                  autoComplete="off"
                  placeholder="Enter Airtech Model"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="bePartNo"
                label="Customer Part No"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Enter Customer Part No"),
                  },
                ]}
              >
                <Input
                  maxLength={100}
                  size="large"
                  disabled={isLoading}
                  autoComplete="off"
                  placeholder="Enter Customer Part No"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="typeOfUnit"
                label="Type of Unit"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Enter Type of Unit"),
                  },
                ]}
              >
                <Input
                  maxLength={100}
                  size="large"
                  disabled={isLoading}
                  autoComplete="off"
                  placeholder="Enter Type of Unit"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="warrantyDuration"
                label="Warranty (Months)"
                rules={[
                  {
                    required: true,
                    message: "Please enter Warranty Duration",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  size="large"
                  style={{ width: "100%" }}
                  disabled={isLoading}
                  placeholder="Enter Warranty Duration"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="price"
                label="Price ($)"
                rules={[
                  {
                    required: true,
                    message: "Please enter Price",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  size="large"
                  style={{ width: "100%" }}
                  disabled={isLoading}
                  placeholder="Enter Price"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="platform"
                label="Platform"
                // rules={[
                //   {
                //     required: true,
                //     validator: requiredFieldValidator("Enter Platform"),
                //   },
                // ]}
              >
                <Input
                  maxLength={100}
                  size="large"
                  disabled={isLoading}
                  autoComplete="off"
                  placeholder="Enter Platform"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="acronym"
                label="Acronym"
                // rules={[
                //   {
                //     required: true,
                //     validator: requiredFieldValidator("Enter Platform"),
                //   },
                // ]}
              >
                <Input
                  maxLength={100}
                  size="large"
                  disabled={isLoading}
                  autoComplete="off"
                  placeholder="Enter Acronym"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="description"
                label="Description"
                // rules={[
                //   {
                //     required: true,
                //     validator: requiredFieldValidator("Enter Description"),
                //   },
                // ]}
              >
                <Input
                  maxLength={100}
                  size="large"
                  disabled={isLoading}
                  autoComplete="off"
                  placeholder="Enter Description"
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              size="large"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              loading={isLoading}
              className="btnDimensions"
            >
              {props.id ? "Update" : "Save"}
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
